import { useCallback, useEffect, useMemo, useState } from "react";
import { RajasthanGenData } from "../../../models/rajasthan";
import {
  getDemoRajasthanGenerationData,
  getDemoRajasthanIsgsIntervalData,
  getDemoRajasthanOutageData,
  getRajasthanGenerationData,
  getRajasthanIsgsIntervalData,
  getRajasthanOutageData,
  getRajReData,
} from "../../Rajasthan/Utility/RestApi";
import { titleCase } from "../../../extensions/extension";
import { getMonthFromDate, months } from "../../../extensions/date";
import { ChartAreaLoader } from "../../../components/Loading";
import FilterMenu from "../../../components/FilterMenu";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ExchangeColors } from "../Exchange3/FormatData";
import { stringToConsistentColor } from "../../../extensions/string";
import { R_CHART_TYPES } from "../const";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";

import {
  calculateTimePeriods,
  DEFAULT_DATES,
  DEFAULT_FINANCIAL_YEARS,
  DEFAULT_QUARTERS,
  DEFAULT_YEARS,
  FilteredOutEntity,
} from "./Data";

import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../Demand/SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  updateDefaultTrendDates,
  updateTrendDateOptions,
} from "../../../store/state/DefaultDatesSetter";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

export const GROUP_BY = ["Total", "State", "Station", "Source", "Sector"];

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "dateIndex", "dateIndex_num"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
  {
    label: "Quarter",
    key: "quarter",
    values: ["quarter"],
    isTime: false,
  },
  {
    label: "Year",
    key: "year",
    values: ["year"],
    isTime: false,
  },
  {
    label: "Financial Year",
    key: "financialYear",
    values: ["financialYear"],
    isTime: false,
  },
];

export const DEMAND_UNITS = ["MW", "MWh"];

const chartSuffixes = [
  {
    suffix: "onBarDS",
    displayName: "onBarDS",
    stackSuffix: "onBarDS",
    key: "Generation",
  },
  {
    suffix: "onBarDc",
    displayName: "onBarDc",
    stackSuffix: "onBarDc",
    key: "Capacity (Declared)",
  },
  {
    suffix: "outage",
    displayName: "Outage",
    stackSuffix: "outage",
    key: "Outage",
  },
];

const RajasthanTrend = () => {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const uniqueDate = DefaultDates[activeTab].trendDateOptions;
  const [loading, setLoading] = useState(false);
  const [uniqueStations, setUniqueStations] = useState<string[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedUnit, setSelectedUnit] = useState(DEMAND_UNITS[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [data, setData] = useState<RajasthanGenData[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);

  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const dispatch = useDispatch();

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    if (DefaultDates[activeTab].defaultTrendDates === undefined) {
      setLoading(false);
      return;
    }

    try {
      setData([]);
      setChartData([]);
      const dates = DefaultDates[activeTab].defaultTrendDates;
      const results = await Promise.all(
        dates.map(async (date: any) => {
          const [startDate, endDate] = date.value.split(",");
          const [outageData, intraData, reGenData, isgsData] =
            await Promise.all([
              getDemoRajasthanOutageData(
                startDate,
                endDate,
                selectedInterval.key === "financialYear"
                  ? "fiscal-year"
                  : selectedInterval.key
              ).then((data) =>
                data.map(
                  (item: {
                    outage_mw: number;
                    outage_date: any;
                    outage_time: any;
                    station_name: string;
                    year: string;
                    quarter: string;
                    fiscal_year: string;
                    month: any;
                  }) => ({
                    ...item,
                    dates: date.value,
                    outage_mw: item.outage_mw || 0,
                    outage_mwh: item.outage_mw / 4 || 0,
                    date: item.outage_date,
                    sector: "Intrastate",
                    slot: item.outage_time,
                    station: item.station_name + " Intrastate",
                    state: "Demo RE",
                    onBarDS_mw: 0,
                    onBarDS_mwh: 0,
                    onBarDc_mw: 0,
                    onBarDc_mwh: 0,
                    backdown_mw: 0,
                    backdown_mwh: 0,
                    year: item.year ?? undefined,
                    quarter: item.quarter
                      ? `Q${item.quarter + "-" + item.year}`
                      : undefined,
                    financialYear: item.fiscal_year
                      ? `FY${parseInt(item.fiscal_year)}-${(
                          parseInt(item.fiscal_year + 1) % 100
                        )
                          .toString()
                          .padStart(2, "0")}`
                      : undefined,
                    month: item.month
                      ? months[Number(item.month) - 1].slice(0, 3) +
                        " " +
                        item.year
                      : undefined,
                  })
                )
              ),

              getDemoRajasthanGenerationData(startDate, endDate).then((data) =>
                data
                  .map((item) => {
                    const timePeriods = calculateTimePeriods(item.date);
                    return {
                      ...item,
                      outage_mw: 0,
                      outage_mwh: 0,
                      sector: "Intrastate",
                      source: titleCase(titleCase(item.source)),
                      station: item.station + " Intrastate",
                      dates: date.value,
                      year: timePeriods.year,
                      quarter: timePeriods.quarter,
                      financialYear: timePeriods.financialYear,
                      month:
                        getMonthFromDate(item.date) + " " + timePeriods.year,
                    };
                  })
                  .filter(
                    (item) =>
                      item.backdown_mw !== 0 ||
                      item.onBarDS_mw !== 0 ||
                      item.onBarDc_mw !== 0
                  )
              ),

              getRajReData({ startDate, endDate }).then((data) =>
                data
                  .map((item) => {
                    const timePeriods = calculateTimePeriods(item.date);
                    return {
                      ...item,
                      source: titleCase(item.energy_type),
                      outage_mw: 0,
                      outage_mwh: 0,
                      onBarDS_mw: item.value_mw,
                      onBarDS_mwh: item.value_mw / 4,
                      onBarDc_mw: 0,
                      onBarDc_mwh: 0,
                      backdown_mw: 0,
                      backdown_mwh: 0,
                      station: `${titleCase(item.energy_type)} RE ${item.area}`,
                      sector:
                        item.area === "InterState"
                          ? "Interstate"
                          : "Intrastate",
                      slot: item.time_block,
                      state: "Demo RE",
                      dates: date.value,
                      year: timePeriods.year,
                      quarter: timePeriods.quarter,
                      financialYear: timePeriods.financialYear,
                      month:
                        getMonthFromDate(item.date) + " " + timePeriods.year,
                    };
                  })
                  .filter((item) => item.area === "IntraState")
              ),

              getDemoRajasthanIsgsIntervalData({
                startDate,
                endDate,
                interval:
                  selectedInterval.key === "financialYear"
                    ? "fiscal-year"
                    : selectedInterval.key,
              }).then((data) =>
                data
                  .map((item) => ({
                    ...item,
                    onBarDS_mw: item.value_mw,
                    outage_mw: 0,
                    outage_mwh: 0,
                    source: titleCase(item.from_gen__generation_source),
                    onBarDS_mwh:
                      selectedInterval.label === "Year" ||
                      selectedInterval.label === "Financial Year" ||
                      selectedInterval.label === "Quarter"
                        ? item.value_mw
                        : item.value_mw / 4,
                    onBarDc_mw: 0,
                    onBarDc_mwh: 0,
                    backdown_mw: 0,
                    backdown_mwh: 0,
                    station: `${item.from_utility__name} Interstate`,
                    sector: "Interstate",
                    dates: date.value,
                    year: item.year ?? undefined,
                    quarter: item.quarter
                      ? `Q${item.quarter}-${item.year}`
                      : undefined,
                    financialYear: item.fiscal_year
                      ? `FY${parseInt(item.fiscal_year)}-${(
                          parseInt(item.fiscal_year + 1) % 100
                        )
                          .toString()
                          .padStart(2, "0")}`
                      : undefined,
                    month: item.month
                      ? months[Number(item.month) - 1].slice(0, 3) +
                        " " +
                        item.year
                      : undefined,
                  }))
                  .filter(
                    (item) =>
                      item.backdown_mw !== 0 ||
                      item.onBarDS_mw !== 0 ||
                      item.onBarDc_mw !== 0
                  )
              ),
            ]);
          return [...reGenData, ...intraData, ...isgsData, ...outageData];
        })
      );

      const newData = results.flat();

      const processedData = newData.map((item) => ({
        ...item,
        dates:
          uniqueDate.find((d: { value: string }) => d.value === item.dates)
            ?.label ?? item.dates,
      }));

      setData(processedData);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    selectedInterval,
    uniqueDate,
    setData,
    setLoading,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
  }

  useEffect(() => {
    let filteredData = data.map((item: any) => ({
      ...item,
      dateIndex_num: parseFloat(item.dateIndex?.replace(" day", "") || "0"),
    }));

    if (selectedInterval.key === "year") {
      filteredData = filteredData.filter((item) => item.year !== undefined);
    }

    if (selectedInterval.key === "financialYear") {
      filteredData = filteredData.filter(
        (item) => item.financialYear !== undefined
      );
    }

    if (selectedInterval.key === "quarter") {
      filteredData = filteredData.filter((item) => item.quarter !== undefined);
    }

    if (selectedInterval.key === "month") {
      filteredData = filteredData.filter((item) => item.month !== undefined);
    }

    const aa = Array.from(new Set(filteredData.map((item) => item.station)));
    setUniqueStations(aa);

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }

    if (selectedStation.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedStation.map((item) => item.value).includes(item.station)
      );
    }
    const selectedFilterStations = selectedStation.filter((item) =>
      FilteredOutEntity.includes(item.label)
    );
    if (selectedFilterStations.length === 0) {
      filteredData = filteredData.filter(
        (item) => !FilteredOutEntity.includes(item.station)
      );
    }

    function handleKey() {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "time") {
          return ["dateIndex", "slot"];
        } else if (selectedInterval.key === "date") {
          return ["dateIndex"];
        } else if (selectedInterval.key === "month") {
          return ["month"];
        }
      } else {
        return selectedInterval.values;
      }
    }

    const par: ChartDataParams = {
      keys: handleKey() ?? [],
      tableKeys: ["dateIndex_num", "slot"],
      groupBy:
        selectedGroupKeys === "Total"
          ? ["dates"]
          : ["dates"].concat(selectedGroupKeys.toLowerCase()),
      volumeKey: "",
      aggregationKeys: [
        "onBarDc_mw",
        "onBarDS_mwh",
        "onBarDS_mw",
        "onBarDc_mwh",
        "outage_mwh",
        "outage_mw",
      ],
      priceKey: "",
      averageKeys:
        selectedInterval.key === "date"
          ? ["onBarDc_mw", "onBarDS_mw", "outage_mw"]
          : ["onBarDc_mw", "onBarDS_mw"],
      reverseSort: ["dateIndex_num", "slot"],
      averageDuplicationKeys: ["dateIndex_num", "slot"],
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedBlock,
    selectedBlock.length,
    selectedGroupKeys,
    selectedInterval,
    selectedStation,
    selectedUnit,
  ]);

  function _handleSuffixChange(index: number) {
    if (selectedSuffix.length > 1) {
      setSelectedSuffix([chartSuffixes[index]]);
    } else {
      if (selectedSuffix.includes(chartSuffixes[index])) {
        return;
      }
      setSelectedSuffix([...selectedSuffix, chartSuffixes[index]]);
    }

    setSelectedSuffix((prev) =>
      prev.sort((a, b) => chartSuffixes.indexOf(a) - chartSuffixes.indexOf(b))
    );
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      return selectedInterval.values[0];
    }
  }, [selectedBlock.length, selectedInterval.key, selectedInterval.values]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    function getName() {
      if (displayName === "onBarDS") {
        return "Generation";
      } else if (displayName === "onBarDc") {
        return "Capacity (Declared)";
      } else if (displayName === "backdown") {
        return "Backdown";
      } else if (displayName === "offBarWarm") {
        return "OffBarWarm";
      } else {
        return displayName;
      }
    }

    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${getName()}`,
      // legendColor: undefined,
      // stroke: stringToConsistentColor(
      //   `${normalizeDisplayLabelWithoutUndefined(key)} ${getName()}`
      // ),
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function handleDefaultDates(index: number) {
    setData([]);
    if (Intervals[index].key === "year") {
      setSelectedBlock([]);
      dispatch(updateTrendDateOptions({ activeTab, dates: DEFAULT_YEARS }));
      dispatch(
        updateDefaultTrendDates({ activeTab, dates: [DEFAULT_YEARS[0]] })
      );
      return;
    }
    if (Intervals[index].key === "quarter") {
      setSelectedBlock([]);
      dispatch(updateTrendDateOptions({ activeTab, dates: DEFAULT_QUARTERS }));
      dispatch(
        updateDefaultTrendDates({ activeTab, dates: [DEFAULT_QUARTERS[3]] })
      );
      return;
    }
    if (Intervals[index].key === "financialYear") {
      setSelectedBlock([]);
      dispatch(
        updateTrendDateOptions({ activeTab, dates: DEFAULT_FINANCIAL_YEARS })
      );
      dispatch(
        updateDefaultTrendDates({
          activeTab,
          dates: [DEFAULT_FINANCIAL_YEARS[1]],
        })
      );
      return;
    }
    if (Intervals[index].key === "month") {
      dispatch(updateTrendDateOptions({ activeTab, dates: DEFAULT_DATES }));
      dispatch(
        updateDefaultTrendDates({
          activeTab,
          dates: [{ value: "2026-01-01,2026-01-31", label: "JAN-26" }],
        }),
      );
      return;
    }
    if (Intervals[index].key === "date" || Intervals[index].key === "time") {
      dispatch(updateTrendDateOptions({ activeTab, dates: DEFAULT_DATES }));
      dispatch(
        updateDefaultTrendDates({
          activeTab,
          dates: [{ value: "2026-01-01,2026-01-31", label: "JAN-26" }],
        })
      );
      return;
    }
  }

  useEffect(() => {
    dispatch(updateTrendDateOptions({ activeTab, dates: DEFAULT_DATES }));
    dispatch(
      updateDefaultTrendDates({
        activeTab,
        dates: [{ value: "2026-01-01,2026-01-31", label: "JAN-26" }],
      })
    );
  }, [activeTab, dispatch]);

  function getLegends() {
    return selectedSuffix
      .filter((item) => item.suffix !== "offBarWarm")
      .flatMap(({ suffix, displayName, stackSuffix }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              selectedInterval,
              selectedChartType as any,
              ExchangeColors
            ) as any
        )
      );
  }

  function summaryExtraKeys() {
    if (selectedInterval.isTime) {
      return ["slot", "dateIndex"];
    } else if (selectedInterval.key === "date") {
      return ["dateIndex"];
    } else {
      return selectedInterval.values;
    }
  }

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value
  );

  const allLegends = useMemo(
    () => getLegends(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartKeys, selectedSuffix, selectedUnit, selectedChartType, selectedInterval]
  );

  const { visibleLegends, handleLegendSelect, selectedLegends: selectedLegendKeys } =
    useLegendFilter(allLegends as any);

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData,
    tableData: tableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Generation Trend Analysis",
      dates: DefaultDates[activeTab].defaultTrendDates,
    }),
  };

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "dateIndex";
      }
      return "dateIndex";
    } else {
      return undefined;
    }
  }

  return (
    <>
      <div className="page-container">
        <div className="gas-page-container">
          <NewFilterContainer>
            <div className="side-filters-container text-nowrap">
              <FilterMenu
                title="Intervals"
                items={Intervals.map((item) => item.label)}
                selectedItems={[selectedInterval.label]}
                onSelect={(item, index) => {
                  setSelectedInterval(Intervals[index]);
                  handleDefaultDates(index);
                }}
              />
              <FilterMenu
                title="Unit"
                items={DEMAND_UNITS}
                selectedItems={[selectedUnit]}
                onSelect={(item, index) => {
                  handleUnitChange(item);
                }}
              />
              <FilterMenu
                title="Variables"
                items={chartSuffixes.map((item) => item.key)}
                selectedItems={selectedSuffix.map((item) => item.key)}
                onSelect={(item, index) => {
                  _handleSuffixChange(index);
                }}
              />
              <FilterMenu
                title="Group By"
                items={GROUP_BY}
                selectedItems={[selectedGroupKeys]}
                onSelect={(item, index) => {
                  _handleGroupByChange(index);
                }}
              />

              <FilterMenu
                title="Chart Type"
                items={R_CHART_TYPES.map((item) => item.label)}
                selectedItems={[selectedChartType.label]}
                onSelect={(item, index) => {
                  setSelectedChartType(R_CHART_TYPES[index]);
                }}
              />
            </div>
          </NewFilterContainer>

          <MainPageContainer>
            <SecondaryHeader />
            <div className="gas-chart-container">
              {loading ? <ChartAreaLoader title="Loading Generation Trend Analysis" subtitle="Fetching Demo State generation trend analysis data…" /> : (<>
              <div className="gas-heading">
                <h2 className="chartHeading w-80">Generation Trend Analysis</h2>
                <div className="flex gap-x-2 w-full justify-end">
                  {selectedGroupKeys === "Station" && (
                    <SearchBox
                      options={uniqueStations
                        .filter((item) => !item.includes("RE IntraState"))
                        .map((item) => {
                          return { value: item, label: item };
                        })}
                      isMany={true}
                      placeholder="Search Station"
                      selectedOptions={selectedStation}
                      onChange={setSelectedStation}
                    />
                  )}

                  {(selectedInterval.key === "time" ||
                    selectedInterval.key === "date" ||
                    (selectedInterval.key === "month" &&
                      !selectedSuffix.some(
                        (suffix) => suffix.suffix === "outage"
                      ))) && (
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                  )}

                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(commonSummaryProps);

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(commonSummaryProps);

                      dataTab.label === "Table" &&
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={allLegends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegendKeys}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
              </>)}
            </div>

            <div className="gna-footer-container">
              <div className="grid grid-cols-2 justify-between gap-5 ">
                <p className="gna-footer-text text-start col-span-1">
                  Powered by <strong>GNA Energy</strong>
                </p>
                <strong className="gna-footer-text col-span-1 text-end">
                  Demo Version
                </strong>
              </div>
            </div>
          </MainPageContainer>
        </div>
      </div>
    </>
  );
};

export default RajasthanTrend;
